<template>
  <div class="mt-8">
    <h2 class="text-2xl font-bold text-white mb-6">
      Oglasi korisnika {{ username }}
    </h2>

    <!-- Search Component -->
    <ProductsSearch
      @filter-change="handleFilterChange"
      :initial-filters="activeFilters"
      ref="searchComponent"
      :hide-fields="['city']"
    />

    <!-- Active Filters -->
    <ActiveFilters
      :filters="activeFilters"
      :filter-labels="filterLabels"
      :categories="categories"
      @remove-filter="removeFilter"
      @reset-filters="resetFilters"
    />

    <!-- Products content container with fixed minimum height -->
    <div class="min-h-[600px] flex flex-col">
      <!-- Loader for products -->
      <div
        v-if="showLoader"
        class="flex justify-center items-center py-20 flex-grow"
      >
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
        ></div>
      </div>

      <!-- Products list - Grid layout -->
      <div
        v-else-if="!loading && products.length > 0"
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 w-full max-w-6xl mx-auto mt-6 flex-grow"
      >
        <router-link
          v-for="product in productsWithCity"
          :key="`product-${currentPage}-${product.id}`"
          :to="{
            name: 'product-detail',
            params: { id: product.slug || product.id },
          }"
          class="block w-full transition-transform duration-200 hover:-translate-y-1"
        >
          <ProductCard :product="product" />
        </router-link>
      </div>

      <!-- No products -->
      <div
        v-else-if="!loading"
        class="bg-gray-800 rounded-lg p-8 text-center text-gray-400 mt-6 flex-grow flex items-center justify-center"
      >
        <p>Korisnik nema dostupnih proizvoda.</p>
      </div>
    </div>

    <!-- Pagination - outside the content container -->
    <div class="mt-8">
      <PaginationComponent
        :meta="meta"
        :current-page="currentPage"
        :update-url="true"
        @page-change="changePage"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  PropType,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
} from 'vue';
import { useRoute } from 'vue-router';
import ProductCard from '@/components/ProductCard.vue';
import ProductsSearch from '@/components/ProductsSearchComponent.vue';
import ActiveFilters from '@/components/ActiveFiltersComponent.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import categoryService from '@/services/categoryService';
import { useProfileProducts } from '@/composables/useProfileProducts';
import { debounce } from '@/utils/debounce';

interface Category {
  id: string;
  name: string;
}

interface Meta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface Filters {
  search?: string;
  categoryId?: string;
  minPrice?: number;
  maxPrice?: number;
}

interface SearchComponentRef {
  updateFilters: (filters: Filters) => void;
  resetFilters: () => void;
}

export default defineComponent({
  name: 'ProfileProducts',
  components: {
    ProductCard,
    ProductsSearch,
    ActiveFilters,
    PaginationComponent,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
    username: {
      type: String,
      required: true,
    },
    userCity: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const route = useRoute();
    const userIdRef = ref(props.userId);
    const categories = ref<Category[]>([]);
    const showLoader = ref(false);
    const filterLabels = {
      search: 'Pretraga',
      categoryId: 'Kategorija',
      minPrice: 'Min. cena',
      maxPrice: 'Max. cena',
    };

    // Debounced loader functions
    const debouncedShowLoader = debounce(() => {
      showLoader.value = true;
    }, 300);

    const hideLoaderImmediately = () => {
      debouncedShowLoader.cancel();
      showLoader.value = false;
    };

    // Computed current page from URL
    const currentPageFromUrl = computed(() => {
      const page = parseInt(route.query.page as string, 10);
      return isNaN(page) || page < 1 ? 1 : page;
    });

    // Watch for userId changes to update the ref
    watch(
      () => props.userId,
      (newUserId) => {
        userIdRef.value = newUserId;
        // Fetch products when userId changes
        if (newUserId) {
          fetchProducts(currentPageFromUrl.value, activeFilters.value);
        }
      }
    );

    // Watch for URL page changes
    watch(
      () => route.query.page,
      (newPage) => {
        const page = parseInt(newPage as string, 10) || 1;
        fetchProducts(page, activeFilters.value);
      }
    );

    const {
      products,
      meta,
      loading,
      error,
      currentPage,
      activeFilters,
      fetchProducts,
      changePage,
      handleFilterChange,
      removeFilter,
      resetFilters,
    } = useProfileProducts(userIdRef);

    // Computed products with city - ensures all products have the user's city
    const productsWithCity = computed(() => {
      return products.value.map((product) => ({
        ...product,
        city: props.userCity, // Override product city with user's city
      }));
    });

    // Watch loading state for debounced spinner
    watch(loading, (isLoading) => {
      if (isLoading) {
        debouncedShowLoader();
      } else {
        hideLoaderImmediately();
      }
    });

    // Debounced page change function - now not needed since PaginationComponent handles URL updates
    const debouncedChangePage = debounce((page: number) => {
      // This will be called by PaginationComponent but won't be used since updateUrl=true
      console.log('Page change event received:', page);
    }, 200);

    // Fetch categories on mount
    onMounted(async () => {
      try {
        categories.value = await categoryService.getCategories();
      } catch (err) {
        console.error('Error fetching categories:', err);
        categories.value = [];
      }

      // Initial fetch of products with current page from URL
      if (props.userId) {
        fetchProducts(currentPageFromUrl.value, {});
      }
    });

    const searchComponent = ref<SearchComponentRef | null>(null);

    const updateSearchComponent = (filters: Filters) => {
      if (searchComponent.value) {
        searchComponent.value.updateFilters(filters);
      }
    };

    const resetSearchComponent = () => {
      if (searchComponent.value) {
        searchComponent.value.resetFilters();
      }
    };

    // Clean up on unmount
    onBeforeUnmount(() => {
      debouncedShowLoader.cancel();
    });

    return {
      products,
      productsWithCity,
      meta,
      loading,
      showLoader,
      error,
      currentPage: currentPageFromUrl, // Use computed page from URL
      activeFilters,
      categories,
      filterLabels,
      changePage: debouncedChangePage,
      handleFilterChange,
      removeFilter,
      resetFilters,
      searchComponent,
    };
  },
});
</script>
